/**
 * One saved pipeline view, one document — whichever button asks for it.
 *
 * The Pipeline toolbar exported the on-screen columns while Reporting's
 * "Run Now" on the same saved view sent only its filters and got the fixed
 * 27-column Debt Pipeline layout, so one report came out as two different
 * documents (CRE-06). Both paths now build their request here, from the view's
 * own columns and filters and the one "Mirror the on-screen columns" setting:
 *
 *   mirror on   the view's columns, rows filtered and ordered as the grid
 *               shows them (PDF sends that matrix as `viewGrid`; Excel is
 *               built locally from the same rows, as the toolbar does)
 *   mirror off  the client-matched Debt Pipeline layout over the view's
 *               filters — the server honours the view vocabulary, 'All' included
 *
 * A view that Report Studio designed carries its design in `filters.report`
 * (sort, grouping, totals, style, title). Run Now and the report editor follow
 * it, as a schedule of the same view does; a view that was never designed keeps
 * the grid's opening order, with no grouping and no totals.
 */
import { markDemoFilename, markDemoSubtitle, markDemoTitle } from '@/config/demoExportMark'
import type { Loan } from '@/types'
import { COLUMN_MAP } from './columns'
import { applyLoanFilters } from './helpers'
import { applyReportFilters } from './reporting'
import { buildViewGrid, exportStyledXlsx } from './reportExport'
import { buildDesignedViewGrid } from './reportLayout'
import {
  DEFAULT_COLUMNS,
  EMPTY_FILTERS,
  normalizeReportConfig,
  type ColumnId,
  type FilterState,
  type ReportConfig,
} from './types'

export type ViewSort = { id: ColumnId; direction: 'asc' | 'desc' }

/** The grid's opening order: largest request first. */
export const DEFAULT_VIEW_SORT: ViewSort = { id: 'loanAmount', direction: 'desc' }

/** The report layout's order within each stage: the deal longest in its stage first (the print's order, 2 Nov 2026). */
export const OLDEST_FIRST_SORT: ViewSort = { id: 'daysInStatus', direction: 'desc' }

export type ExportableView = {
  name?: string | null
  columns?: readonly string[] | null
  filters?: Partial<FilterState> | Record<string, unknown> | null
}

export function viewFilters(view: ExportableView): FilterState {
  return { ...EMPTY_FILTERS, ...((view.filters ?? {}) as Partial<FilterState>) }
}

export function viewColumns(view: ExportableView): ColumnId[] {
  const known = (view.columns ?? []).filter((id): id is ColumnId => COLUMN_MAP.has(id as ColumnId))
  return known.length ? known : [...DEFAULT_COLUMNS]
}

export function viewTitle(view: ExportableView): string {
  return view.name?.trim() || 'Pipeline Report'
}

/** The Report Studio design a saved view carries, or null for a view that was never designed. */
export function savedReportConfig(view: ExportableView): ReportConfig | null {
  const raw = (view.filters as { report?: unknown } | null | undefined)?.report
  return raw && typeof raw === 'object' ? normalizeReportConfig(raw as Partial<ReportConfig>) : null
}

/** The order a saved view opens in: its design's sort, else the grid's own. */
export function savedViewSort(view: ExportableView): ViewSort {
  const config = savedReportConfig(view)
  return config ? { id: config.sortId, direction: config.sortDir } : DEFAULT_VIEW_SORT
}

/** The rows the grid shows for these filters. */
export function filterViewLoans(loans: Loan[], filters: FilterState): Loan[] {
  return applyReportFilters(applyLoanFilters(loans, filters), filters.report)
}

/** Equal values keep one order everywhere — the grid, every export and the
 *  server's view grid for schedules — rather than whatever order each list
 *  arrived in (two $62M deals printed in opposite orders). */
export function byDealId(a: Pick<Loan, 'id'>, b: Pick<Loan, 'id'>): number {
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

/** The grid's sort — the same comparison DealGrid's header applies. */
export function sortViewLoans(loans: Loan[], sort: ViewSort): Loan[] {
  const column = COLUMN_MAP.get(sort.id)
  const rows = [...loans]
  rows.sort((a, b) => {
    const left = column?.value(a) ?? ''
    const right = column?.value(b) ?? ''
    const order =
      typeof left === 'number' && typeof right === 'number'
        ? sort.direction === 'asc' ? left - right : right - left
        : sort.direction === 'asc'
          ? String(left).localeCompare(String(right))
          : String(right).localeCompare(String(left))
    return order || byDealId(a, b)
  })
  return rows
}

/** Whether the rows being exported are the illustrative sample (demo mode). Such an
 *  export says so in its title, under it and in its filename (config/demoExportMark.ts). */
export type DemoExportOption = { demo?: boolean }

/** The design with its title and subtitle carrying the demo mark. A live export's
 *  design is returned as it is. */
function markedDesign(design: ReportConfig, fallbackTitle: string, demo: boolean): ReportConfig {
  if (!demo) return design
  return {
    ...design,
    title: markDemoTitle(design.title.trim() || fallbackTitle, true),
    subtitle: markDemoSubtitle(design.subtitle, true),
  }
}

/** Filters for POST /reports/generate that render this view. */
export function savedViewReportFilters(
  view: ExportableView,
  loans: Loan[],
  mirrorColumns: boolean,
  sort?: ViewSort,
  options: DemoExportOption = {},
): Record<string, unknown> {
  const demo = options.demo ?? false
  const filters = viewFilters(view)
  const title = markDemoTitle(viewTitle(view), demo)
  const columns = viewColumns(view)
  const saved = savedReportConfig(view)
  const design = saved ? markedDesign(saved, viewTitle(view), demo) : null
  const payload: Record<string, unknown> = { ...filters, viewName: title, columns }
  // The server reads the design's title and subtitle beside the matrix.
  if (demo && design) payload.report = design
  if (mirrorColumns) {
    const rows = sortViewLoans(filterViewLoans(loans, filters), sort ?? savedViewSort(view))
    if (design) {
      // The design's grouping, totals and numbers travel with the matrix so
      // the file is laid out as the Studio lays it out.
      payload.viewGrid = buildDesignedViewGrid(rows, columns, design.title.trim() || title, design)
    } else {
      const grid = buildViewGrid(rows, columns, title)
      payload.viewGrid = { headers: grid.headers, rows: grid.rows }
    }
  }
  return payload
}

/** How one saved view exports: the toolbar's local workbook, or a server
 *  render with these filters. Run Now and the report editor both follow it,
 *  so every button on a saved view gives the one document. */
export type SavedViewExportPlan =
  | { kind: 'workbook'; rows: Loan[] }
  | { kind: 'server'; filters: Record<string, unknown> }

export function savedViewExportPlan(
  view: ExportableView,
  loans: Loan[],
  format: 'pdf' | 'xlsx',
  mirrorColumns: boolean,
  options: DemoExportOption = {},
): SavedViewExportPlan {
  if (mirrorColumns && format === 'xlsx') {
    return { kind: 'workbook', rows: sortViewLoans(filterViewLoans(loans, viewFilters(view)), savedViewSort(view)) }
  }
  return { kind: 'server', filters: savedViewReportFilters(view, loans, mirrorColumns, undefined, options) }
}

/** The on-screen Excel for this view — the workbook the toolbar builds. A view
 *  Report Studio designed is built with its design (grouping, totals, style). */
export async function exportSavedViewXlsx(
  view: ExportableView,
  rows: Loan[],
  filenameBase: string,
  sort: ViewSort = savedViewSort(view),
  columns: ColumnId[] = viewColumns(view),
  options: DemoExportOption = {},
): Promise<void> {
  const demo = options.demo ?? false
  const title = markDemoTitle(viewTitle(view), demo)
  const saved = savedReportConfig(view)
  const design = saved ? markedDesign(saved, viewTitle(view), demo) : null
  await exportStyledXlsx(
    rows,
    columns,
    design
      ? { ...design, sortId: sort.id, sortDir: sort.direction }
      : normalizeReportConfig({
          title,
          subtitle: markDemoSubtitle(`${rows.length} deal${rows.length === 1 ? '' : 's'} · on-screen columns`, demo),
          sortId: sort.id,
          sortDir: sort.direction,
          groupBy: '',
          showTotals: false,
          columnFilters: {},
          exportTheme: 'print',
        }),
    markDemoFilename(filenameBase, demo),
    title,
  )
}
